import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery, publicBaseQuery } from '@core/api/baseQuery';
import {
  preferredOrganizationUuid,
  setActiveOrganization,
} from '@features/organization/store/organizationSlice';
import { logout, setCredentials } from '../store/authSlice';
import type { LoginRequest, LoginResponse, MeResponse } from '../types';

/**
 * Endpoint-aware base query.
 *
 * `login` goes through `publicBaseQuery`: it must never carry an `Authorization`
 * header, because a stale token from a previous session would be sent alongside
 * the credentials and the resulting 401 would be misread by the authenticated
 * base query as "the current session expired" (logging the user out mid-login).
 * Every other endpoint uses the authenticated base query.
 */
const authBaseQuery: typeof baseQuery = (args, api, extraOptions) =>
  api.endpoint === 'login'
    ? // Both are base queries over the same fetch; only the error shape differs
      // (raw `FetchBaseQueryError` vs. the normalized `ApiError`). The value is
      // passed through untouched — `apiErrorMessage` reads either.
      (publicBaseQuery(args, api, extraOptions) as unknown as ReturnType<typeof baseQuery>)
    : baseQuery(args, api, extraOptions);

/**
 * Auth API using RTK Query.
 *
 * User-scoped (never tenant-scoped), so its cache is NOT reset when the active
 * organization changes.
 */
export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: authBaseQuery,
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (credentials) => ({
        url: '/api/auth/login/',
        method: 'POST',
        body: credentials,
      }),
      /**
       * The single place a session starts: store the credentials, then pick the
       * organization to act on.
       *
       * Order matters — the store's `setCredentials` listener wipes the previous
       * user's tenant state (including the active organization), so the
       * selection has to be dispatched *after* it.
       */
      async onQueryStarted(_credentials, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const organizations = data.organizations ?? [];
          dispatch(setCredentials({ user: data.user, token: data.token, organizations }));

          const active = preferredOrganizationUuid(organizations, data.user.default_organization_uuid);
          if (active) {
            dispatch(setActiveOrganization(active));
          }
        } catch {
          // Bad credentials / network failure: the form surfaces the rejection.
        }
      },
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: '/api/auth/logout/',
        method: 'POST',
      }),
      /**
       * Deleting the token server-side is best effort: an already expired or
       * rotated token answers 401, and the local session must go either way.
       */
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // Ignored on purpose — see above.
        }
        dispatch(logout());
      },
    }),
    /**
     * `GET /api/auth/me/` returns `{ user, organizations, memberships }` — the
     * caller wrapped alongside their tenancy context, NOT a bare user object.
     */
    me: builder.query<MeResponse, void>({
      query: () => ({
        url: '/api/auth/me/',
        method: 'GET',
      }),
    }),
  }),
});

// Only the hooks something actually calls are exported. `me` keeps its endpoint
// definition — it mirrors a real backend route and its response type is pinned by
// `core/types/__tests__/apiContract.test.ts` — and is reachable as
// `authApi.endpoints.me` until a screen needs it.
export const { useLoginMutation, useLogoutMutation } = authApi;
